import type { WsMessage } from "@/api/websocket";
import type { FrameApplyContext } from "@/lib/companion-frame-reducer";
import {
  createWorkToastItem, newestWorkToast, payloadString, silentStateChangedAt, workToastTaskTitle,
} from "@/lib/work-toast-item";
import { applyToolApprovalRequested, applyToolApprovalTaskUpdate } from "@/lib/work-toast-tool-approvals";

type Payload = Record<string, unknown>;

// Frame delivery is at-least-once (mirror windows replay `notification.new`).
// Every branch below that creates an item first checks the store that owns
// the fact it would otherwise be resurrecting: tasksStore for a task that has
// left "blocked"; a tool approval checks the ended ids in
// `work-toast-tool-approvals`.

function applyTaskBlocked(data: Payload, ctx: FrameApplyContext, now: number): void {
  const taskId = payloadString(data, "source_id");
  if (!taskId) return;
  const store = ctx.workToastStore.getState();
  const item = newestWorkToast(store.items, (c) => c.taskId === taskId && c.source !== "approval" && c.source !== "task");
  if (item) {
    if (item.state !== "blocked") store.patchItem(item.key, { state: "blocked", stateBeforeBlocked: item.state, stateChangedAt: now, dismissed: false });
    return;
  }
  const key = `task:${taskId}`;
  if (store.items[key]) return;
  // The tasks store only holds the currently-open project's fetched tasks:
  // a task not found there is unknown, not known-unblocked (another
  // project, or arriving before the initial fetch resolves), so only
  // suppress creation when the store demonstrably says otherwise.
  const task = ctx.tasksStore.getState().tasks.find((candidate) => candidate.id === taskId);
  if (task && task.status !== "blocked") return;
  store.upsertItem(createWorkToastItem({
    key, source: "task", tracked: false, state: "blocked", agentName: payloadString(data, "agent_name") ?? "Agent",
    projectId: payloadString(data, "project_id"), taskId, detail: workToastTaskTitle(ctx, taskId) ?? payloadString(data, "title") ?? "",
  }, now));
}


function applyUnblocked(taskId: string | null, ctx: FrameApplyContext, now: number): void {
  if (!taskId) return;
  const store = ctx.workToastStore.getState();
  for (const item of Object.values(store.items)) {
    if (item.taskId !== taskId || item.state !== "blocked") continue;
    if (item.source === "task") {
      store.removeItem(item.key);
      continue;
    }
    const state = item.stateBeforeBlocked ?? "working";
    store.patchItem(item.key, { state, stateBeforeBlocked: null, stateChangedAt: silentStateChangedAt(state, now), dismissed: false });
  }
}

export function applyWorkToastNotificationEvent(message: WsMessage, ctx: FrameApplyContext, now: number): boolean {
  const data = message.data;
  switch (message.type) {
    case "notification.new":
      if (payloadString(data, "notification_type") === "task_blocked") applyTaskBlocked(data, ctx, now);
      return true;
    case "direct_session.approval_requested":
      applyToolApprovalRequested(data, ctx, now);
      return true;
    case "task.updated":
      applyToolApprovalTaskUpdate(data, ctx);
      return true;
    case "task.unblocked":
      applyUnblocked(payloadString(data, "task_id"), ctx, now);
      return true;
    case "task.status_changed":
      if (data.status !== "blocked") applyUnblocked(payloadString(data, "task_id"), ctx, now);
      return true;
    default:
      return false;
  }
}
